import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { BsTrash } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { timeElapsed } from "../../services/getTimeSended";
import { jeSupprimeLeMessageInbox } from "../../services/sendMessageInbox";
import { jeSupprimeLeMessageDeGroupe } from "../../services/sendMessageGroupe";
import { DisplayDiscussionForMiniScreenContext } from "../../contexts/DisplayDiscussionForMiniScreenContext";
import Person from "../Person";
import { AuthContext } from "../../contexts/AuthContext";
import { WorkspaceContext } from "../../contexts/WorkspaceContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import MeetingInviteCard from "./MeetingInviteCard";
import FileGrid from "./FileGrid";
import ExpandableText from "./ExpandableText";
import AudioPlayer from "../audio/AudioPlayer";
import { normalizeFileContenu } from "../../services/normalizeContenu";
import { disintegrateElement } from "../../services/disintegrate";
import { includesId, idsEqual } from "../../services/idUtils";
import "./chat-reply.css";

gsap.registerPlugin(Draggable);

const SWIPE_THRESHOLD = 50;

function ReplyQuoteBlock({ replyToId, replyToContenu, replyToSender, replyToNature }) {
  if (!replyToId) return null;
  const isAudio = replyToNature === "messageVocal";
  const authorName = isAudio ? "Message audio" : (replyToSender?.displayName || "L'utilisateur");
  const preview = replyToContenu || "";
  const label = replyToNature === "fichier" ? "Fichier" : replyToNature === "lien" ? "Lien" : replyToNature === "code_meeting" ? "Réunion" : replyToNature === "messageVocal" ? "Message audio" : "";

  return (
    <div className="chat-reply-quote">
      <span className="chat-reply-quote-author">{isAudio ? label : authorName}</span>
      {!isAudio && (
        <span className="chat-reply-quote-text">
          {label && !preview ? label : preview.length > 80 ? preview.slice(0, 80) + "..." : preview}
        </span>
      )}
    </div>
  );
}

export default function YourMessage({
  contenu,
  object,
  isGroupe,
  groupe,
  onOpenGallery,
  onDelete,
  onSwipeReply,
}) {
  const { theme } = useContext(ThemeContext);
  const [color, setColor] = useState(theme === "dark" ? "#fff" : "#000");
  const { destinataire } = useContext(DisplayDiscussionForMiniScreenContext);
  const { user } = useContext(AuthContext);
  const { currentUserRole, currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const isGuest = currentUserRole === "guest";
  const location = useNavigate();
  const bubbleRef = useRef(null);
  const rowRef = useRef(null);

  const vocalContent = object.nature === "messageVocal" ? (Array.isArray(object.contenu) ? object.contenu[0] : object.contenu) : null;

  const emetteurMembre = useMemo(() => {
    if (!isGroupe || !groupe?.membres) return null;
    return groupe.membres.find((m) => idsEqual(m.userId, object.emetteur)) || null;
  }, [isGroupe, groupe?.membres, object.emetteur]);

  useEffect(() => {
    if (theme === "dark") {
      setColor("#fff");
    } else {
      setColor("#000");
    }
  }, [theme]);

  useEffect(() => {
    if (!rowRef.current || !onSwipeReply) return;

    const el = rowRef.current;
    const drag = Draggable.create(el, {
      type: "x",
      bounds: { minX: 0, maxX: 80 },
      edgeResistance: 0.85,
      onDragEnd() {
        const x = this.x;
        if (x >= SWIPE_THRESHOLD && object) {
          onSwipeReply(object);
        }
        gsap.to(el, { x: 0, duration: 0.25, ease: "power2.out" });
      },
    })[0];

    return () => drag.kill();
  }, [onSwipeReply, object]);

  return (
    <>
      <div
        className={`tyn-reply-item incoming ${
          includesId(object.deletedBy, user.userId) ? "d-none" : ""
        }`}
      >
        {isGroupe && emetteurMembre ? (
          <div className="tyn-reply-avatar">
            <div
              className="tyn-media tyn-size-md tyn-circle"
              onClick={() => location(`/contact/${emetteurMembre.username}`)}
            >
              <Person
                person={{
                  image: emetteurMembre.userImage || "",
                  nom: emetteurMembre.nom || "",
                  prenom: emetteurMembre.prenom || "",
                  colors: emetteurMembre.color,
                  userId: emetteurMembre.userId,
                }}
              />
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className="tyn-reply-group">
          <div className="chat-swipe-wrapper">
            {object.replyToId && (
              <ReplyQuoteBlock
                replyToId={object.replyToId}
                replyToContenu={object.replyToContenu}
                replyToSender={object.replyToSender}
                replyToNature={object.replyToNature}
              />
            )}
            <div
              className="tyn-reply-bubble"
              ref={bubbleRef}
              style={onSwipeReply ? { touchAction: "pan-y", userSelect: "none" } : undefined}
            >
              <div
                className={`tyn-reply-text mon-text-toujours-dans-la-div`}
                style={{
                  position: "relative",
                  color,
                }}
                ref={rowRef}
              >
                {object.nature === "code_meeting" ? (
                  <MeetingInviteCard code={contenu} isOwn={false} />
                ) : object.nature === "texte" ? (
                  <ExpandableText>{contenu}</ExpandableText>
                ) : object.nature === "fichier" ? (
                  <FileGrid files={normalizeFileContenu(object.contenu)} onOpenGallery={onOpenGallery} />
                ) : object.nature === "lien" ? (
                  object.contenu[object.contenu.length - 1]
                ) : object.nature === "messageVocal" ? (
                  <>
                    <AudioPlayer fileUrl={vocalContent?.fileUrl} isOwn={false} duree={vocalContent?.duree} peaks={vocalContent?.peaks} />
                    <div
                      style={{
                        position: "absolute",
                        right: "8px",
                        bottom: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {vocalContent?.duree > 0 && (
                        <span className="meta" style={{ fontSize: "10px" }}>
                          {Math.floor(vocalContent.duree / 60) + ":" + (Math.floor(vocalContent.duree) % 60).toString().padStart(2, "0")}
                        </span>
                      )}
                      <span className="meta" style={{ fontSize: "10px" }}>
                        {timeElapsed(object.timeUnix)}
                      </span>
                    </div>
                  </>
                ) : (
                  <></>
                )}

                {object.nature !== "messageVocal" && (
                  <div
                    style={{
                      position: "absolute",
                      right: "4px",
                      bottom: `${object.nature === "fichier" ? "2.5px" : "0.3px"}`,
                    }}
                    className="text-primary"
                  >
                    <span className="meta" style={{ fontSize: "10px" }}>
                      {timeElapsed(object.timeUnix)}
                    </span>
                  </div>
                )}
              </div>
              {isGroupe && emetteurMembre && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "-15px",
                    fontSize: "11px",
                    color,
                  }}
                >
                  {emetteurMembre.prenom}
                </div>
              )}
              {!isGuest && (
                <ul className="tyn-reply-tools">
                  <li className="dropup-center">
                    <button
                      className="btn btn-icon btn-sm btn-transparent btn-pill"
                      data-bs-toggle="dropdown"
                      onClick={() => {
                        if (bubbleRef.current) {
                          disintegrateElement(bubbleRef.current);
                        }
                        if (onDelete) onDelete(object.messageId);
                        if (isGroupe) {
                          jeSupprimeLeMessageDeGroupe(
                            user,
                            object.destinataire,
                            object.messageId,
                            object.canal,
                            workspaceId
                          );
                        } else {
                          jeSupprimeLeMessageInbox(
                            user,
                            destinataire,
                            object.messageId,
                            workspaceId
                          );
                        }
                      }}
                    >
                      <BsTrash
                        style={{ fontSize: "20px", height: "auto", width: "auto" }}
                      />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {isGroupe ? <br /> : <></>}
    </>
  );
}
